/**
 * RZL FIBU/EA booking import ("Datenimport Buchungen, Euro-Version"), after RZL's own manual
 * (Handbuch Datenimport, August 2026): 41 positional fields, no header row, ";" between fields,
 * CR/LF, Windows-1252, dates TTMMJJJJ, decimal comma.
 *
 *  - Every booking balances: the Personenkonto (or Kassa) carries the gross, the expense/revenue
 *    rows the net plus the tax in "Steuerbetrag" (RZL books the tax account itself). An ER puts
 *    the net in Soll and the gross in Haben, an AR the other way round (examples 1 and 20).
 *  - One rate is two rows of Buchungsart 1. Several rates - or a tip, which has no VAT - are a
 *    split: the gross on the Personenkonto with Gegenkonto 0 (Buchungsart 4), then one row per
 *    rate (Buchungsart 3), tax fields zero on the collective row (example 32b).
 *  - Reverse charge and ig. Erwerb book the self-assessed tax on the expense row while the
 *    supplier gets the net (examples 28 and 30); that is the one exception to "gross = net + tax".
 *  - The tax is described by three fields: USt-Schlüssel (the rate, or 1/2/3 for export, ig.
 *    Lieferung, EU service), USt-Code (1 Vorsteuer, 2 MwSt, 3 Erwerbsteuer) and Sondercode
 *    (05 reverse charge with input tax, 22 Kleinunternehmer, 13 other exempt turnover).
 */
import type { DocumentRecord } from "@/lib/api/documents";
import { round2, type TaxTreatment } from "@/lib/at/vat";
import { money } from "@/lib/export/tables";
import {
  clean,
  dateParts,
  decimal,
  isVatId,
  numericBelegnr,
  prepareBookings,
  type BookingContext,
  type BookingProblem,
} from "./common";
import type { PlainBooking } from "./money-bookings";

type RzlTax = { key: (rate: number) => string; code: string; special: string };

const byRate = (rate: number) => (rate === 4.9 ? "49" : decimal(rate));
const fixed = (key: string) => () => key;

/** USt-Schlüssel, USt-Code and Sondercode per direction and treatment (manual, ch. 2.2.2). */
const RZL_TAX: Record<"in" | "out", Record<TaxTreatment, RzlTax>> = {
  in: {
    standard: { key: byRate, code: "1", special: "0" },
    reverse_charge: { key: byRate, code: "1", special: "05" },
    eu_intra_community: { key: byRate, code: "3", special: "0" },
    // An invoice from outside the EU carries no Austrian VAT; import VAT is a document of its own.
    export: { key: fixed("0"), code: "1", special: "0" },
    small_business: { key: fixed("0"), code: "1", special: "0" },
    no_vat: { key: fixed("0"), code: "1", special: "0" },
  },
  out: {
    standard: { key: byRate, code: "2", special: "0" },
    export: { key: fixed("1"), code: "2", special: "0" },
    eu_intra_community: { key: fixed("2"), code: "2", special: "0" },
    // Our reverse charge invoices are services to EU businesses (example 15).
    reverse_charge: { key: fixed("3"), code: "2", special: "0" },
    small_business: { key: fixed("1"), code: "2", special: "22" },
    no_vat: { key: fixed("1"), code: "2", special: "13" },
  },
};

/** RZL rejects an ig. Lieferung or EU service without the customer's UID. */
const needsVatId = (document: DocumentRecord) =>
  document.kind === "AR" &&
  (document.taxTreatment === "eu_intra_community" || document.taxTreatment === "reverse_charge");

/** Treatments where the supplier charged no VAT but we owe and deduct it ourselves. */
const selfAssessed = (treatment: TaxTreatment, outgoing: boolean) =>
  !outgoing && (treatment === "reverse_charge" || treatment === "eu_intra_community");

export function rzlDate(value: string | undefined): string {
  const parts = dateParts(value);
  return parts ? parts.join("") : "";
}

type Row = {
  konto: string;
  gegenkonto: string;
  soll: number;
  haben: number;
  steuer: number;
  key: string;
  code: string;
  special: string;
  buchungsart: string;
};

export function rzlBookings(
  documents: DocumentRecord[],
  context: BookingContext,
): { rows: string[][]; problems: BookingProblem[] } {
  const { bookable, problems } = prepareBookings(documents, context, needsVatId);
  const rows: string[][] = [];

  for (const { document, account, counterAccount, lines, tip } of bookable) {
    const outgoing = document.kind === "AR";
    const treatment = document.taxTreatment ?? "standard";
    const tax = RZL_TAX[outgoing ? "out" : "in"][treatment];
    const owed = selfAssessed(treatment, outgoing);
    const op = document.kind === "KA" ? "0" : numericBelegnr(document);

    const booked = [
      ...lines.map((line) => ({
        net: line.net,
        // Reverse charge and ig. Erwerb: the tax we owe ourselves, not what the supplier charged.
        tax: owed ? round2((line.net * line.rate) / 100) : line.vat,
        charged: owed ? 0 : line.vat,
        key: tax.key(line.rate),
        code: tax.code,
        special: tax.special,
      })),
      ...(tip > 0
        ? [{ net: tip, tax: 0, charged: 0, key: "0", code: outgoing ? "2" : "1", special: "0" }]
        : []),
    ];
    const gross = round2(booked.reduce((sum, line) => sum + line.net + line.charged, 0));
    const split = booked.length > 1;
    const out: Row[] = [];

    // The Personenkonto (or Kassa) side: gross, no tax.
    const personal = {
      konto: counterAccount,
      gegenkonto: split ? "0" : account,
      soll: outgoing ? gross : 0,
      haben: outgoing ? 0 : gross,
      steuer: 0,
    };
    const lineRow = (line: (typeof booked)[number], buchungsart: string): Row => ({
      konto: account,
      gegenkonto: counterAccount,
      soll: outgoing ? 0 : line.net,
      haben: outgoing ? line.net : 0,
      steuer: line.tax,
      key: line.key,
      code: line.code,
      special: line.special,
      buchungsart,
    });

    if (split) {
      out.push({ ...personal, key: "0", code: "0", special: "0", buchungsart: "4" });
      for (const line of booked) out.push(lineRow(line, "3"));
    } else {
      // In RZL's examples an AR starts on the customer, an ER on the expense account.
      const only = booked[0]!;
      const counter = {
        ...personal,
        key: only.key,
        code: only.code,
        special: only.special,
        buchungsart: "1",
      };
      if (outgoing) out.push(counter, lineRow(only, "1"));
      else out.push(lineRow(only, "1"), counter);
    }

    const vatId = needsVatId(document)
      ? (document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase()
      : "";
    for (const row of out)
      rows.push(rzlRow(document, op, row, isVatId(vatId) ? vatId : "", context));
  }
  return { rows, problems };
}

function rzlRow(
  document: DocumentRecord,
  op: string,
  row: Row,
  vatId: string,
  context: BookingContext,
): string[] {
  const fields = new Array<string>(41).fill("");
  fields[0] = row.konto;
  fields[1] = row.gegenkonto;
  fields[2] = op;
  fields[3] = rzlDate(document.issueDate);
  fields[5] = "EUR";
  fields[6] = money(row.soll);
  fields[7] = money(row.haben);
  fields[8] = money(row.steuer);
  fields[10] = "0,00";
  fields[11] = "0,00";
  fields[12] = "0";
  fields[13] = document.kind;
  fields[14] = clean(document.docNumber ?? "", 15);
  fields[15] = "1"; // USt-Land Österreich
  fields[16] = row.key;
  fields[17] = row.code;
  fields[18] = row.special;
  fields[19] = row.buchungsart;
  fields[23] = clean(document.partnerName ?? "", 40);
  fields[25] = vatId;
  fields[31] = clean(document.invoiceNumber ?? "", 19);
  fields[36] = context.documentPath(document);
  return fields;
}

/** No header row: RZL reads fields by position. */
export function rzlFile(rows: string[][]): string {
  return rows.map((row) => row.join(";")).join("\r\n") + "\r\n";
}

/** Kontenart by number range in the ÖKR scheme (manual, ch. 4.2.1). */
export function rzlAccountKind(number: number): string {
  const range = Math.floor((number >= 100000 ? number / 10 : number) / 10000);
  return { 2: "5", 3: "7", 4: "6", 5: "8", 6: "9", 7: "11", 8: "10", 9: "12" }[range] ?? "";
}

/** "Mariahilfer Straße 12, 1070 Wien" -> street, postcode, town. */
function splitAddress(address: string): { street: string; zip: string; town: string } {
  const parts = address.split(",").map((part) => part.trim());
  const last = parts.pop() ?? "";
  const match = /^(\d{4,5})\s+(.+)$/.exec(last);
  if (!match) return { street: address, zip: "", town: "" };
  return { street: parts.join(", "), zip: match[1]!, town: match[2]! };
}

/** Personenkonten (ch. 4): 19 fields, loaded before or together with the bookings. */
export function rzlPartnerFile(
  accounts: Array<{ number: number; name?: string; address?: string; vatId?: string }>,
): string {
  const lines = [...accounts]
    .sort((a, b) => a.number - b.number)
    .map((account) => {
      const { street, zip, town } = splitAddress(account.address ?? "");
      const vatId = (account.vatId ?? "").replace(/\s+/g, "").toUpperCase();
      const fields = new Array<string>(19).fill("");
      fields[0] = String(account.number);
      fields[1] = rzlAccountKind(account.number);
      fields[3] = isVatId(vatId) ? vatId : "";
      fields[5] = clean(account.name ?? "", 40);
      fields[6] = clean(street, 40);
      fields[7] = clean(town, 25);
      fields[12] = /^\d{4}$/.test(zip) ? "A" : "";
      fields[13] = zip;
      return fields.join(";");
    });
  return lines.join("\r\n") + "\r\n";
}

/**
 * Bookings between two Sachkonten (`moneyBookings`) as RZL's two balancing rows of Buchungsart 1:
 * Soll on the debit account, Haben on the credit account, no tax.
 */
export function rzlPlainRows(bookings: PlainBooking[]): string[][] {
  const rows: string[][] = [];
  for (const booking of bookings) {
    for (const [konto, gegenkonto, soll, haben] of [
      [booking.debit, booking.credit, booking.amount, 0],
      [booking.credit, booking.debit, 0, booking.amount],
    ] as const) {
      const fields = new Array<string>(41).fill("");
      fields[0] = konto;
      fields[1] = gegenkonto;
      fields[2] = "0";
      fields[3] = rzlDate(booking.date);
      fields[5] = "EUR";
      fields[6] = money(soll);
      fields[7] = money(haben);
      fields[8] = "0,00";
      fields[10] = "0,00";
      fields[11] = "0,00";
      fields[12] = "0";
      fields[13] = booking.symbol;
      fields[14] = booking.belegnr;
      fields[15] = "1";
      fields[16] = "0";
      fields[17] = "1";
      fields[18] = "0";
      fields[19] = "1";
      fields[23] = clean(booking.text, 40);
      rows.push(fields);
    }
  }
  return rows;
}
